import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Send, Palette, CreditCard, Mail, Building2, ToggleRight } from "lucide-react";

const SECTION_LABELS = {
  branding: { label: "Branding", icon: Palette },
  stripe: { label: "Stripe", icon: CreditCard },
  smtp: { label: "SMTP", icon: Mail },
  company: { label: "Entreprise", icon: Building2 },
  features: { label: "Features", icon: ToggleRight },
};

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/settings");
    setSettings(data);
    setDrafts({});
  };
  useEffect(() => { load(); }, []);

  const v = (section, key) => {
    const d = drafts[section] || {};
    return key in d ? d[key] : (settings?.[section]?.[key] ?? "");
  };
  const setV = (section, key, value) => {
    setDrafts((d) => ({ ...d, [section]: { ...(d[section] || {}), [key]: value } }));
  };
  const isDirty = (section) => Boolean(drafts[section] && Object.keys(drafts[section]).length);

  const save = async (section) => {
    if (!isDirty(section)) return;
    setSaving((s) => ({ ...s, [section]: true }));
    try {
      const payload = { ...drafts[section] };
      if (section === "smtp" && "port" in payload) payload.port = parseInt(payload.port, 10) || 587;
      await api.patch(`/admin/settings/${section}`, payload);
      toast.success("Configuration enregistrée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving((s) => ({ ...s, [section]: false }));
    }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Renseignez une adresse de test");
    try {
      const { data } = await api.post("/admin/settings/test-email", { to: testTo, locale: "fr" });
      if (data.ok) toast.success("Email de test envoyé");
      else toast.error(data.error || "Échec de l'envoi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!settings) return <div className="font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="admin-settings-page">
      <div className="border-b border-border pb-4">
        <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// Settings</div>
        <h1 className="mt-1 font-display text-2xl font-black tracking-tight">Configuration globale</h1>
        <p className="mt-1 text-sm text-muted-foreground">Toutes les clés sensibles sont stockées chiffrées en base. Aucun secret n'est exposé côté frontend.</p>
      </div>

      <Tabs defaultValue="branding" className="mt-6">
        <TabsList className="rounded-sm">
          {Object.entries(SECTION_LABELS).map(([k, info]) => (
            <TabsTrigger key={k} value={k} className="rounded-sm gap-2" data-testid={`settings-tab-${k}`}>
              <info.icon className="h-3.5 w-3.5" /> {info.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* BRANDING */}
        <TabsContent value="branding" className="mt-4 space-y-4">
          <Field label="Nom de l'app" testid="branding-app-name"><Input value={v("branding", "app_name")} onChange={(e) => setV("branding", "app_name", e.target.value)} /></Field>
          <Field label="Tagline (FR)"><Input value={v("branding", "tagline_fr")} onChange={(e) => setV("branding", "tagline_fr", e.target.value)} /></Field>
          <Field label="Tagline (EN)"><Input value={v("branding", "tagline_en")} onChange={(e) => setV("branding", "tagline_en", e.target.value)} /></Field>
          <Field label="Couleur primaire (hex)"><Input value={v("branding", "primary_color")} onChange={(e) => setV("branding", "primary_color", e.target.value)} placeholder="#002FA7" /></Field>
          <Field label="URL du logo (PNG/SVG hébergé)"><Input value={v("branding", "logo_url")} onChange={(e) => setV("branding", "logo_url", e.target.value)} placeholder="https://..." /></Field>
          <Field label="Email de contact (utilisé dans les pages légales)"><Input value={v("branding", "contact_email")} onChange={(e) => setV("branding", "contact_email", e.target.value)} placeholder="contact@example.com" /></Field>
          <Field label="Destinataires du formulaire de contact (emails séparés par virgule)"><Input value={v("branding", "contact_recipients")} onChange={(e) => setV("branding", "contact_recipients", e.target.value)} placeholder="info@savebox.fr, support@savebox.fr" /></Field>
          <Field label="URL de support"><Input value={v("branding", "support_url")} onChange={(e) => setV("branding", "support_url", e.target.value)} placeholder="https://help..." /></Field>
          <SaveBar dirty={isDirty("branding")} saving={saving.branding} onSave={() => save("branding")} testid="save-branding" />
        </TabsContent>

        {/* STRIPE */}
        <TabsContent value="stripe" className="mt-4 space-y-4">
          <Toggle label="Stripe activé" checked={Boolean(v("stripe", "enabled"))} onChange={(b) => setV("stripe", "enabled", b)} testid="stripe-enabled" />
          <div>
            <Label className="font-mono text-[11px] uppercase text-muted-foreground">Mode</Label>
            <Select value={v("stripe", "mode") || "test"} onValueChange={(val) => setV("stripe", "mode", val)}>
              <SelectTrigger className="mt-1 rounded-sm h-10" data-testid="stripe-mode"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-sm">
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="live">Live (production)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label="Secret key (sk_test_... ou sk_live_...)"><Input type="password" value={v("stripe", "secret_key")} onChange={(e) => setV("stripe", "secret_key", e.target.value)} placeholder="••••••••" /></Field>
          <Field label="Publishable key (optionnel, pk_...)"><Input value={v("stripe", "publishable_key")} onChange={(e) => setV("stripe", "publishable_key", e.target.value)} /></Field>
          <Field label="Webhook secret (whsec_...)"><Input type="password" value={v("stripe", "webhook_secret")} onChange={(e) => setV("stripe", "webhook_secret", e.target.value)} /></Field>
          <p className="font-mono text-[11px] text-muted-foreground">Si Secret key vide → utilisation de la variable d'environnement STRIPE_API_KEY.</p>
          <SaveBar dirty={isDirty("stripe")} saving={saving.stripe} onSave={() => save("stripe")} testid="save-stripe" />
        </TabsContent>

        {/* SMTP */}
        <TabsContent value="smtp" className="mt-4 space-y-4">
          <Toggle label="SMTP activé" checked={Boolean(v("smtp", "enabled"))} onChange={(b) => setV("smtp", "enabled", b)} testid="smtp-enabled" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Host"><Input value={v("smtp", "host")} onChange={(e) => setV("smtp", "host", e.target.value)} placeholder="smtp.gmail.com" /></Field>
            <Field label="Port"><Input type="number" value={v("smtp", "port") === "" ? "" : String(v("smtp", "port") ?? 587)} onChange={(e) => setV("smtp", "port", e.target.value)} /></Field>
            <Field label="Username"><Input value={v("smtp", "username")} onChange={(e) => setV("smtp", "username", e.target.value)} /></Field>
            <Field label="Password"><Input type="password" value={v("smtp", "password")} onChange={(e) => setV("smtp", "password", e.target.value)} /></Field>
            <Field label="From email"><Input value={v("smtp", "from_email")} onChange={(e) => setV("smtp", "from_email", e.target.value)} placeholder="noreply@example.com" /></Field>
            <Field label="From name"><Input value={v("smtp", "from_name")} onChange={(e) => setV("smtp", "from_name", e.target.value)} /></Field>
            <Field label="Reply-To (optionnel)"><Input value={v("smtp", "reply_to")} onChange={(e) => setV("smtp", "reply_to", e.target.value)} /></Field>
            <div className="flex items-end">
              <Toggle label="Utiliser STARTTLS" checked={Boolean(v("smtp", "use_tls"))} onChange={(b) => setV("smtp", "use_tls", b)} testid="smtp-tls" />
            </div>
          </div>
          <SaveBar dirty={isDirty("smtp")} saving={saving.smtp} onSave={() => save("smtp")} testid="save-smtp" />

          <div className="mt-4 border border-border bg-accent/30 p-4">
            <div className="font-mono text-[11px] uppercase text-muted-foreground">Envoyer un email de test</div>
            <div className="mt-2 flex gap-2">
              <Input placeholder="destinataire@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="rounded-sm" data-testid="smtp-test-to" />
              <Button onClick={sendTest} className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid="smtp-test-send"><Send className="h-4 w-4" /> Envoyer</Button>
            </div>
          </div>
        </TabsContent>

        {/* COMPANY */}
        <TabsContent value="company" className="mt-4 space-y-4">
          <Field label="Raison sociale"><Input value={v("company", "legal_name")} onChange={(e) => setV("company", "legal_name", e.target.value)} /></Field>
          <Field label="Adresse"><Input value={v("company", "address")} onChange={(e) => setV("company", "address", e.target.value)} /></Field>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="SIRET"><Input value={v("company", "siret")} onChange={(e) => setV("company", "siret", e.target.value)} /></Field>
            <Field label="TVA intra"><Input value={v("company", "vat_number")} onChange={(e) => setV("company", "vat_number", e.target.value)} /></Field>
            <Field label="Pays"><Input value={v("company", "country")} onChange={(e) => setV("company", "country", e.target.value)} /></Field>
          </div>
          <SaveBar dirty={isDirty("company")} saving={saving.company} onSave={() => save("company")} testid="save-company" />
        </TabsContent>

        {/* FEATURES */}
        <TabsContent value="features" className="mt-4 space-y-4">
          <Toggle label="Emails de rapport de sauvegarde (uniquement si fichiers copiés ou échec)" checked={Boolean(v("features", "send_run_reports"))} onChange={(b) => setV("features", "send_run_reports", b)} testid="feat-run-reports" />
          <Toggle label="Récapitulatif quotidien à 9h00 (1 seul email/jour avec le bilan 24h)" checked={Boolean(v("features", "send_daily_digest"))} onChange={(b) => setV("features", "send_daily_digest", b)} testid="feat-daily-digest" />
          <Toggle label="Email de bienvenue" checked={Boolean(v("features", "send_welcome_email"))} onChange={(b) => setV("features", "send_welcome_email", b)} testid="feat-welcome" />
          <Toggle label="Email fin d'essai imminente" checked={Boolean(v("features", "send_trial_ending_email"))} onChange={(b) => setV("features", "send_trial_ending_email", b)} testid="feat-trial-ending" />
          <Toggle label="Emails liés au paiement" checked={Boolean(v("features", "send_payment_emails"))} onChange={(b) => setV("features", "send_payment_emails", b)} testid="feat-payment" />
          <SaveBar dirty={isDirty("features")} saving={saving.features} onSave={() => save("features")} testid="save-features" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div>
      <Label className="font-mono text-[11px] uppercase text-muted-foreground">{label}</Label>
      <div className="mt-1" data-testid={testid}>{React.cloneElement(children, {
        className: `${children.props.className || ""} rounded-sm`,
        autoComplete: "off",
        "data-bwignore": "true",
        "data-lpignore": "true",
        "data-1p-ignore": "true",
        "data-form-type": "other",
      })}</div>
    </div>
  );
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <Label className="flex items-center gap-3 font-mono text-xs">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} /> {label}
    </Label>
  );
}
function SaveBar({ dirty, saving, onSave, testid }) {
  return (
    <div className="flex justify-end pt-2">
      <Button disabled={!dirty || saving} onClick={onSave} className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid={testid}>
        <Save className="h-4 w-4" /> {saving ? "..." : "Enregistrer"}
      </Button>
    </div>
  );
}
